import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import { useAuth } from '../../context/AuthContext';
    import SafeIcon from '../../common/SafeIcon';
    import DealFormModal from './DealFormModal';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiEdit2, FiTrash2, FiClock, FiCheckCircle, FiAlertCircle } = FiIcons;

    const UserDealsTab = () => {
      const { user } = useAuth();
      const [deals, setDeals] = useState([]);
      const [loading, setLoading] = useState(true);
      const [showFormModal, setShowFormModal] = useState(false);
      const [editingDeal, setEditingDeal] = useState(null);

      const fetchUserDeals = async () => {
        if (!user) return;
        try {
          setLoading(true);
          const { data, error } = await supabase.from('deals').select('*, profiles(name)').eq('user_id', user.id).order('created_at', { ascending: false });
          if (error) throw error;
          setDeals(data || []);
        } catch (error) {
          console.error('Error fetching user deals:', error);
        } finally {
          setLoading(false);
        }
      };

      useEffect(() => {
        fetchUserDeals();
      }, [user]);

      const handleAddNew = () => {
        setEditingDeal(null);
        setShowFormModal(true);
      };

      const handleEdit = (deal) => {
        setEditingDeal(deal);
        setShowFormModal(true);
      };

      const handleDelete = async (dealId) => {
        if (window.confirm('Are you sure you want to delete this deal?')) {
          try {
            const { error } = await supabase.from('deals').delete().eq('id', dealId);
            if (error) throw error;
            fetchUserDeals();
          } catch (error) {
            console.error('Error deleting deal:', error);
          }
        }
      };
      
      const getStatusChip = (status) => {
        switch (status) {
          case 'approved': return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><SafeIcon icon={FiCheckCircle} className="mr-1.5" /> Approved</span>;
          case 'rejected': return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><SafeIcon icon={FiAlertCircle} className="mr-1.5" /> Rejected</span>;
          default: return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><SafeIcon icon={FiClock} className="mr-1.5" /> Pending</span>;
        }
      };

      return (
        <div>
          <div className="flex justify-end mb-6">
            <button onClick={handleAddNew} className="flex items-center space-x-2 bg-yellow-500 text-white px-5 py-2.5 rounded-lg hover:bg-yellow-600 transition-colors shadow">
              <SafeIcon icon={FiPlus} />
              <span>Post New Deal</span>
            </button>
          </div>
          {loading ? ( <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div></div>) : 
          deals.length === 0 ? (
            <div className="text-center bg-white border border-gray-200 rounded-lg py-16">
              <h3 className="text-xl font-medium text-gray-900 mb-2">No deals yet!</h3>
              <p className="text-gray-600">Click "Post New Deal" to add one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deals.map((deal) => (
                <motion.div key={deal.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between hover:shadow-md transition-shadow">
                  <div className="flex-1 mb-4 sm:mb-0">
                     <div className="flex items-center space-x-4">
                        <h3 className="text-lg font-semibold text-gray-900">{deal.title}</h3>
                        {getStatusChip(deal.status)}
                    </div>
                    <p className="text-sm text-gray-600">For: {deal.profiles?.name || 'N/A'}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleEdit(deal)} className="p-2 text-yellow-500 hover:text-yellow-600 rounded-md hover:bg-yellow-50 transition-colors"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => handleDelete(deal.id)} className="p-2 text-red-600 hover:text-red-700 rounded-md hover:bg-red-50 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          {showFormModal && <DealFormModal deal={editingDeal} onClose={() => setShowFormModal(false)} onSuccess={fetchUserDeals} />}
        </div>
      );
    };

    export default UserDealsTab;